/**
 * `isOpenNow` — derive open/closed status for a StoreLocation from its
 * `opening_hours` jsonb at a specific instant.
 *
 * Shape contract — see `OpeningHours` in
 * `backend/src/db/schema.ts`/`packages/shared/src/types/retailer.ts`. Each
 * weekday is either an `{ open: 'HH:MM', close: 'HH:MM' }` window or
 * `{ closed: true }`. `null` overall (or undefined) is treated as
 * "hours unknown" and reports `closed` with no `nextOpenAt`.
 *
 * Online stores short-circuit to `{ status: 'online' }`. Callers that pass an
 * online store should set `isOnline = true` regardless of `opening_hours`.
 *
 * Midnight wrap: a window where `close <= open` (e.g. 22:00 → 02:00) is
 * treated as crossing into the next day. The current implementation handles
 * the `close === '24:00'` shorthand by clamping to end-of-day.
 *
 * "Closing soon" threshold: 30 minutes by default; pass `closingSoonMinutes`
 * to override.
 *
 * The implementation is deliberately pure — no `Date.now()` reads — so it
 * stays unit-testable with deterministic fixtures. Callers pass the
 * `instant`; the surrounding hook reads the wall clock once per render.
 */

import type { OpeningHours, OpeningHoursDay } from '@shopista/shared/types';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
type DayKey = (typeof DAY_KEYS)[number];

export type IsOpenNowResult =
  | { status: 'open' }
  | { status: 'closing-soon'; minutesUntil: number }
  | { status: 'closed'; nextOpenAt?: Date }
  | { status: 'online' };

export interface IsOpenNowOptions {
  /** Closing-soon threshold in minutes. Defaults to 30. */
  closingSoonMinutes?: number;
  /** Force the online short-circuit. Pass the StoreLocation's `is_online`. */
  isOnline?: boolean;
}

const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_CLOSING_SOON = 30;

/**
 * Parse an `HH:MM` 24-hour string to total minutes since midnight. Accepts
 * `24:00` as end-of-day (1440). Returns NaN for malformed input — callers
 * should guard with isFinite.
 */
function parseHHMM(s: string): number {
  if (s === '24:00') return MINUTES_PER_DAY;
  const m = /^(\d{2}):(\d{2})$/.exec(s);
  if (!m) return NaN;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return NaN;
  if (h < 0 || h > 23 || min < 0 || min > 59) return NaN;
  return h * 60 + min;
}

function dayKeyForOffset(baseDayIdx: number, offsetDays: number): DayKey {
  // Modulo math always lands in 0..6, but TS's noUncheckedIndexedAccess
  // narrows tuple access to `T | undefined`. Cast back to the exact tuple
  // element type — the index is provably in range.
  return DAY_KEYS[(baseDayIdx + offsetDays + 7) % 7] as DayKey;
}

/**
 * Build a Date at `instant`'s local-day-start plus `dayOffset` days plus
 * `minutesIntoDay` minutes. Used for `nextOpenAt` and end-of-window
 * calculations. Operates in the local timezone of `instant` — a deliberate
 * choice; the spec keeps timezone handling out of scope here.
 */
function dateAt(instant: Date, dayOffset: number, minutesIntoDay: number): Date {
  const d = new Date(
    instant.getFullYear(),
    instant.getMonth(),
    instant.getDate() + dayOffset,
    0,
    0,
    0,
    0,
  );
  d.setMinutes(minutesIntoDay);
  return d;
}

interface OpenWindow {
  open: number;
  close: number;
}

function asOpenWindow(day: OpeningHoursDay | undefined | null): OpenWindow | null {
  if (!day || 'closed' in day) return null;
  const open = parseHHMM(day.open);
  const close = parseHHMM(day.close);
  if (!Number.isFinite(open) || !Number.isFinite(close)) return null;
  return { open, close };
}

export function isOpenNow(
  hours: OpeningHours | null | undefined,
  instant: Date,
  opts: IsOpenNowOptions = {},
): IsOpenNowResult {
  if (opts.isOnline) return { status: 'online' };

  if (!hours) {
    // Hours unknown — surface as closed without a `nextOpenAt`.
    return { status: 'closed' };
  }

  const closingSoon = opts.closingSoonMinutes ?? DEFAULT_CLOSING_SOON;
  const dayIdx = instant.getDay(); // 0=Sun .. 6=Sat
  const minutesNow = instant.getHours() * 60 + instant.getMinutes();

  // 1. Yesterday's window may have wrapped past midnight into "today < open
  //    cutoff". Check that first so we attribute the right window.
  const yesterdayKey = dayKeyForOffset(dayIdx, -1);
  const yesterdayWin = asOpenWindow(hours[yesterdayKey]);
  if (yesterdayWin && yesterdayWin.close <= yesterdayWin.open) {
    // Wraps past midnight. Effective "today portion" runs 00:00 → close.
    const effectiveClose = yesterdayWin.close;
    if (minutesNow < effectiveClose) {
      const minutesUntilClose = effectiveClose - minutesNow;
      if (minutesUntilClose <= closingSoon) {
        return { status: 'closing-soon', minutesUntil: minutesUntilClose };
      }
      return { status: 'open' };
    }
  }

  // 2. Today's window. `dayIdx` is 0..6 from `Date.getDay()`, so the tuple
  // access is provably in-range — cast past noUncheckedIndexedAccess.
  const todayKey = DAY_KEYS[dayIdx] as DayKey;
  const todayWin = asOpenWindow(hours[todayKey]);
  if (todayWin) {
    const wraps = todayWin.close <= todayWin.open;
    const effectiveClose = wraps ? MINUTES_PER_DAY : todayWin.close;
    if (minutesNow >= todayWin.open && minutesNow < effectiveClose) {
      const minutesUntilClose = effectiveClose - minutesNow;
      // Suppress closing-soon for wrap windows AND for "open until midnight"
      // (close == 24:00) windows. In both cases the natural follow-on is the
      // next day's window, not a real close — flagging "closing soon" at
      // 23:35 in a 24-hour store would be wrong.
      const closesAtMidnight = todayWin.close === MINUTES_PER_DAY;
      if (!wraps && !closesAtMidnight && minutesUntilClose <= closingSoon) {
        return { status: 'closing-soon', minutesUntil: minutesUntilClose };
      }
      return { status: 'open' };
    }
  }

  // 3. Closed. Find next opening within the next 7 days.
  for (let offset = 0; offset < 7; offset++) {
    const key = dayKeyForOffset(dayIdx, offset);
    const win = asOpenWindow(hours[key]);
    if (!win) continue;
    if (offset === 0 && minutesNow >= win.open) continue; // already past today's open
    const nextOpenAt = dateAt(instant, offset, win.open);
    return { status: 'closed', nextOpenAt };
  }

  return { status: 'closed' };
}

// ---------------------------------------------------------------------------
// Display helpers (T12.0.1.7.2 — additive; do not change `isOpenNow` above).
//
// These keep the StoresCard render free of `Intl` so the unit tests remain
// deterministic across CI locales. `formatNextOpen` returns "Mon 09:00";
// `formatTodayHours` returns "09:00–17:00" (en-dash) or "Closed today" or
// `null` when the day key is absent / malformed.
// ---------------------------------------------------------------------------

const SHORT_DAY_LABELS: Record<DayKey, string> = {
  sun: 'Sun',
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
};

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Format a next-open Date as `{ShortDay} {HH:MM}` in the local timezone of
 * the same Date. `_instant` is accepted for symmetry with potential
 * "today/tomorrow" relative formatting in the future; the current
 * implementation always returns the short day label so badges stay stable
 * across midnight rollovers in tests.
 */
export function formatNextOpen(date: Date, _instant?: Date): string {
  const dayKey = DAY_KEYS[date.getDay()] as DayKey;
  return `${SHORT_DAY_LABELS[dayKey]} ${pad2(date.getHours())}:${pad2(
    date.getMinutes()
  )}`;
}

/**
 * Format today's window as `{HH:MM}–{HH:MM}` (en-dash, U+2013) or
 * `"Closed today"`. Returns `null` when `hours` is null/undefined or the
 * today key is missing — the caller suppresses the "Today:" line in that
 * case.
 */
export function formatTodayHours(
  hours: OpeningHours | null | undefined,
  instant: Date
): string | null {
  if (!hours) return null;
  const dayKey = DAY_KEYS[instant.getDay()] as DayKey;
  const day = hours[dayKey];
  if (!day) return null;
  if ('closed' in day) return 'Closed today';
  // Validate shape — return null if malformed so the UI shows nothing rather
  // than a misleading garbage string.
  if (
    !Number.isFinite(parseHHMM(day.open)) ||
    !Number.isFinite(parseHHMM(day.close))
  ) {
    return null;
  }
  return `${day.open}–${day.close}`;
}
